import type { MilkdownPlugin } from '@milkdown/kit/ctx';
import { $node, $remark } from '@milkdown/kit/utils';
import type { Link, Paragraph, Root, Text } from 'mdast';
import { visit } from 'unist-util-visit';

import { isVideoFile } from '../../nodes/video/video';

export const ATTACHMENT_NODE_NAME = 'attachment';

function isAttachmentRedirectUrl(url: string): boolean {
  const lower = url.toLowerCase();

  return lower.includes('/api/attachments.redirect') || lower.includes('/brainfish-s3/') || lower.includes('x-amz-signature');
}

function parseAttachmentLinkText(text: string): { title: string; size: string } {
  const parts = text.trim().split(/\s+/);
  const size = parts.pop() ?? '0';

  return { title: parts.join(' '), size };
}

/**
 * Platform markdown-it converts `[title size](/api/attachments.redirect?...)` paragraphs
 * into attachment block nodes. Mirror that in remark for collab template loads.
 */
export function transformAttachmentLinksInMdast(tree: Root): void {
  visit(tree, 'paragraph', (node: Paragraph, index, parent) => {
    if (index === undefined || !parent) return;
    if (node.children.length !== 1 || node.children[0].type !== 'link') return;

    const link = node.children[0] as Link;
    const href = link.url;
    if (!isAttachmentRedirectUrl(href) || isVideoFile(href)) return;

    const textContent = link.children
      .filter((child): child is Text => child.type === 'text')
      .map((child) => child.value)
      .join('');
    const { title, size } = parseAttachmentLinkText(textContent);

    // Video attachment links use WxH size tokens and are handled by the video remark plugin.
    if (size.includes('x')) return;

    (parent.children as Root['children'])[index] = {
      type: 'attachmentNode',
      attributes: {
        id: null,
        href,
        title,
        size,
      },
    } as unknown as Root['children'][number];
  });
}

export const remarkAttachmentPlugin = $remark('platform-attachment-transform', () => {
  return () => (tree: Root) => {
    transformAttachmentLinksInMdast(tree);
  };
});

/** Platform `attachment` block atom — attrs must match shared/editor/nodes/Attachment.tsx for Y.js sync. */
export const attachmentNode = $node(ATTACHMENT_NODE_NAME, () => ({
  group: 'block',
  atom: true,
  defining: true,
  attrs: {
    id: { default: null },
    href: { default: null },
    title: { default: '' },
    size: { default: '0' },
  },
  parseDOM: [
    {
      tag: 'a.attachment',
      getAttrs: (dom) => {
        const el = dom as HTMLAnchorElement;

        return {
          id: el.id || null,
          href: el.getAttribute('href'),
          title: el.textContent ?? '',
          size: el.dataset.size ?? '0',
        };
      },
    },
  ],
  toDOM: (node) => [
    'a',
    {
      class: 'attachment',
      href: node.attrs.href,
      'data-size': node.attrs.size,
    },
    node.attrs.title,
  ],
  parseMarkdown: {
    match: (node) => node.type === 'attachmentNode',
    runner: (state, node, type) => {
      const attrs = (node as { attributes?: Record<string, unknown> }).attributes ?? {};

      state.addNode(type, {
        id: attrs.id ?? null,
        href: attrs.href ?? null,
        title: attrs.title ?? '',
        size: String(attrs.size ?? '0'),
      });
    },
  },
  toMarkdown: {
    match: (node) => node.type.name === ATTACHMENT_NODE_NAME,
    runner: (state, node) => {
      const title = (node.attrs.title as string) || '';
      const size = String(node.attrs.size ?? '0');
      const href = node.attrs.href as string;
      const linkText = `${title} ${size}`.trim();

      state.openNode('paragraph');
      state.openNode('link', undefined, { url: href });
      state.addNode('text', undefined, linkText);
      state.closeNode();
      state.closeNode();
    },
  },
}));

export const attachmentPlugins = [remarkAttachmentPlugin, attachmentNode] as unknown as MilkdownPlugin[];
